import PropTypes from 'prop-types';
import React from 'react';

import { Button, OnboardingCard, Modal, ToastContext } from '@weezevent/nacre';

import LoadingCard from '../../../components/loadingCard';
import BankModel from '../../../models/bank';
import RateModel from '../../../models/rateModel';
import EventModel from '../../../models/eventModel';
import { OrganizationMustBeConfirmedModal } from '../../../components/template/contextMenu/event/organizationMustBeConfirmedModal';
import Styles from '../../../styles/event/dashboard/eventDashboard.module.css';
import OrganizationInfos from '../../../models/organizationInfos';

import { fetcher } from '@weezevent/weezjs-core';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSegment } from '../../../components/tracking/trackingProvider';
import { useUser } from '../../../simple-connect';
import useAppContext from '../../../context/useAppContext';

const checkHasRequiredFields = (obj, values) => values.reduce((total_state, field) => obj[field]?.length > 0 && total_state, true);

export default function OnboardingChecklist({ organization, event }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const { track } = useSegment();
    const { user } = useUser();
    const history = useHistory();

    const [{ reloadEvent: reloadEventContext }] = useAppContext();

    const [modal, showModal] = React.useState(false);

    // loading bank, rates and organizationInfos data
    const [bank, loadingBank] = BankModel.useApiModel(
        {
            cache: false,
            id: organization.id
        },
        [organization]
    );
    const [rates, loadingRates] = RateModel.useApiModel(
        {
            cache: false,
            organizationId: organization.id,
            eventId: event.id,
            query: {
                limit: 1000
            }
        },
        [organization, event]
    );
    const [organizationInfos, loadingOrganizationInfos] = OrganizationInfos.useApiModel(
        {
            cache: false,
            organizationId: organization.id
        },
        [organization]
    );

    const organization_required_fields = ['name', 'organization_type', 'first_name', 'last_name', 'phone_number'];
    const address_required_fields = ['address1', 'city', 'country', 'zip_code'];
    const event_required_fields = ['name', 'subtype', 'start_date'];
    const banking_required_fields = ['bic_swift', 'titulaire', 'valid'];

    const organization_ready = React.useMemo(() => {
        return checkHasRequiredFields(organization, organization_required_fields) && checkHasRequiredFields(organization.address, address_required_fields);
    }, [organization, organizationInfos]);
    const bank_ready = React.useMemo(() => {
        return bank && checkHasRequiredFields(bank, banking_required_fields) && (bank.iban?.length > 0 || bank.rbc_account?.length > 0) && Number(bank.valid);
    }, [bank]);

    const [rates_ready, rates_nb] = React.useMemo(() => {
        if (rates && rates.length > 0) {
            const total_count = rates?.reduce((count_visible, rate) => count_visible + (rate?.id && !rate.disabled ? 1 : 0), 0);
            return [total_count > 0, total_count];
        }
        return [false, 0];
    }, [rates]);
    const event_ready = React.useMemo(() => {
        return event && checkHasRequiredFields(event, event_required_fields);
    }, [event]);

    const loading = React.useMemo(() => {
        return !organization?.id || !event?.id || loadingBank || loadingOrganizationInfos || loadingRates;
    }, [organization, event, loadingBank, loadingRates, loadingOrganizationInfos]);

    const resendMailAccountActivation = React.useCallback(async () => {
        try {
            await fetcher.post(`/ticket/organizations/${organization.id}/users/${user.user_id}/activation_email`);
            toast.success(t('ticket.event.sendMailAccountActivationModal.toast.success'));
        } catch {
            toast.error(t('ticket.event.sendMailAccountActivationModal.toast.error'));
        } finally {
            showModal(false);
        }
    }, [user, organization]);

    const canPublish = React.useCallback(() => {
        if (!organizationInfos) {
            return false;
        }
        if (
            organizationInfos?.can_publish_errors?.includes('account_not_validated') ||
            (!organizationInfos.is_owner && organizationInfos?.can_publish_errors?.includes('organization_not_validated'))
        ) {
            showModal(true);
            return false;
        }
        if (organizationInfos?.can_publish_errors?.length > 0) {
            console.error(organizationInfos?.can_publish_errors);
            toast.warning(t('ticket.creationEvent.toast.cannotPublish'));
            return false;
        }
        if (!organizationInfos?.can_publish) {
            toast.warning(t('ticket.creationEvent.toast.cannotPublish'));
            return false;
        }
        return true;
    }, [organizationInfos]);

    const handlePublishTrack = React.useCallback(() => {
        let values = {
            'Page Title': 'HOME EVENT',
            'Event Name': event.name,
            'Event Start Date': event.start_date,
            'Event End Date': event.end_date,
            'Event Type name': event.subtype,
            'Event Published Count': 1
        };

        track('ACTION > PUBLISH EVENT', values, {
            'Event Published List': [event.id],
            'Last Event Published Date': new Date().toUTCString()
        });
    }, [track, event]);

    const onPublishEvent = React.useCallback(() => {
        if (canPublish()) {
            let eventObject = new EventModel({
                id: event.id,
                publication_status: 'published'
            });
            eventObject
                .save({ organizationId: organization.id })
                .then(() => {
                    handlePublishTrack();
                    history.push(`/ticket/O${organization.id}/E${event.id}/dashboard`);
                    toast.success(t('ticket.eventDashboard.onboardingChecklist.success'));
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('eventList.status.toast-error'));
                })
                .finally(() => {
                    reloadEventContext(false);
                });
        }
    }, [organization, event, rates, bank, organizationInfos, handlePublishTrack]);

    // Yes, this is absolute madness, but still, there is no other way to reach Nacre's inner components,
    // So hate me if you want, i can live with it, because i will forever bear the burden of an abomination
    // Such as this one. And if you remove it one day, give me a call, i will buy you a beer!

    const clickHelp = React.useCallback(
        ({ target }) => {
            if (target.className === 'form-link-event') {
                track('ACTION > NEED HELP ONBOARDING', { 'Page Title': 'HOME EVENT' });
            }
        },
        [track]
    );

    React.useEffect(() => {
        window.addEventListener('click', clickHelp);
        return () => {
            window.removeEventListener('click', clickHelp);
        };
    }, [clickHelp]);

    return (
        <OnboardingCard
            className={Styles['onboardingChecklist__container']}
            icon="rocket"
            title={t('ticket.eventDashboard.onboardingChecklist.title')}
            subtitle={t('ticket.eventDashboard.onboardingChecklist.label')}
            footer={
                <a target={'_blank'} rel="noopener noreferrer" className="form-link-event" href={t('ticket.eventDashboard.onboardingChecklist.help_link')}>
                    {t('ticket.eventDashboard.onboardingChecklist.help')}
                </a>
            }
            sectionCards={
                loading
                    ? []
                    : [
                          {
                              href: `/ticket/O${organization.pk}/organization/profile`,
                              title: t('ticket.eventDashboard.onboardingChecklist.organizerData.label'),
                              checked: organization_ready,
                              children: t('ticket.eventDashboard.onboardingChecklist.organizerData.description')
                          },
                          {
                              href: `/ticket/O${organization.pk}/organization/bank`,
                              title: t('ticket.eventDashboard.onboardingChecklist.bankData.label'),
                              checked: bank_ready,
                              children: t('ticket.eventDashboard.onboardingChecklist.bankData.description')
                          },
                          {
                              href: `/ticket/O${organization.pk}/E${event.pk}/settings/event`,
                              title: t('ticket.eventDashboard.onboardingChecklist.eventData.label'),
                              checked: event_ready,
                              children: t('ticket.eventDashboard.onboardingChecklist.eventData.description')
                          },
                          {
                              href: `/ticket/O${organization.pk}/E${event.pk}/settings/rates`,
                              title: t('ticket.eventDashboard.onboardingChecklist.ticketTypes.label', { count: rates_nb }),
                              checked: rates_ready,
                              children: t('ticket.eventDashboard.onboardingChecklist.ticketTypes.description')
                          }
                      ]
            }
        >
            <LoadingCard loading={loading}>
                {user?.email === organization?.email ? (
                    <Modal
                        size="large"
                        open={modal}
                        fullscreenOnMobile={false}
                        allowExternalClosing={false}
                        onClose={() => showModal(false)}
                        title={<span>{t('ticket.eventDashboard.onboardingChecklist.emailModal.title')}</span>}
                        submitButton={<Button primary onClick={() => resendMailAccountActivation()} label={t('ticket.eventDashboard.onboardingChecklist.emailModal.resend')} />}
                        cancelButton={<Button onClick={() => showModal(false)} label={t('ticket.common.action.back')} />}
                    >
                        {t('ticket.eventDashboard.onboardingChecklist.emailModal.description')}
                    </Modal>
                ) : (
                    <OrganizationMustBeConfirmedModal open={modal} onClose={() => showModal(false)} />
                )}

                <Button primary fluid disabled={event.publication_status === 'published' || !organizationInfos} onClick={() => onPublishEvent()}>
                    {t('ticket.eventDashboard.onboardingChecklist.publish')}
                </Button>
                <Button
                    onClick={() => {
                        track('ACTION > CUSTOMIZE MINISITE', { 'Page Title': 'HOME EVENT' });
                        history.push(`/ticket/O${organization.pk}/E${event.pk}/channels/page`);
                    }}
                    fluid
                >
                    {t('ticket.eventDashboard.onboardingChecklist.miniSite')}
                </Button>
            </LoadingCard>
        </OnboardingCard>
    );
}

OnboardingChecklist.propTypes = {
    event: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    organization: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    prefixUrl: PropTypes.string
};
